import React, { useContext, useState } from "react";
import {
  HeaderBase,
  HeaderUpperFlexbox,
  HeaderLowerFlexbox,
  MobileLinks,
  CustomIcon,
  SearchCloseIcon,
  SearchBarContainer,
  Search,
  SearchBarBackground,
  SearchOverlay,
  LogoLinkWrapper,
  LoadingBlock,
  LoadingPanel,
  DropdownCustomOption,
  DropdownCustomContent,
  CustomButton,
  LoginButton,
  CustomFlow,
  UserNav,
  HeaderLinksWrapper,
  SearchToggle,
  LogoContainer,
  MobileSearchButton,
  AccountButton,
} from "./header.styled";
import { isMobile } from "react-device-detect";
import { HeaderCtAsNodeFragment } from "../../types/graphql";
import HeaderLinks from "../header-links/header-links";
import { IStructuredHeaderLinksData } from "../header-links/pure-header-links";
import HeaderCTAs from "../header-ctas/header-ctas";
import { track } from "../../utils/analytics";
import loadable from "@loadable/component";
import * as FA from "../../styles/fontawesome";
import { UserValidationContext } from "../../utils/user-validation-context";
import { Dropdown } from "@xometry/xometry_loft";
import { workcenterBaseDomain } from "../../utils/constants";
import { useThemeContext } from "../../utils/theme-context";
import { LogoDesktop } from "./logos";

// Code split search for header to optimize common-js bundle
const InstantSearchBox = loadable(() => import("./header-search-box"));

export interface IHeaderProps {
  headerLinksUID: string;
  headerCTAsUID: string;
  disableSearch: boolean;
  showAnnouncementBanner: boolean;
}

export const LoadingSearchSkeletonCard = () => (
  <LoadingPanel>
    <LoadingBlock />
  </LoadingPanel>
);

// The header component reders the logo, drichTexters, links,
// and ctas all in one component. Data for the header
// links and header ctas are sourced within their own
// respective components, but are set as state in this
// component so that they can be passed down to the header
// accordion.
const Header = (props: IHeaderProps) => {
  const noLinks = props.headerLinksUID === "no-links";
  const [isAccordionOpen, setAccordionOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const user = useContext(UserValidationContext);

  // State variables for child component data.
  // Values for these are set by passing the set
  // function to the child.
  const [headerLinksData, setHeaderLinksData] =
    useState<IStructuredHeaderLinksData | null>(null);
  const [headerCTAsData, setHeaderCTAsData] =
    useState<HeaderCtAsNodeFragment | null>(null);

  const { currentMode, setCurrentMode, currentTransitionPercent } =
    useThemeContext();

  return (
    <>
      {/* Preload searchbox script on hover interaction with header */}
      <HeaderBase
        onMouseOver={() => InstantSearchBox.preload()}
        isDarkMode={currentMode}
        transitionPercent={currentTransitionPercent}
        isSearchOpen={isSearchOpen}
      >
        <HeaderUpperFlexbox>
          <LogoContainer>
            <LogoLinkWrapper
              isDarkMode={currentMode}
              transitionPercent={currentTransitionPercent}
              to="/"
            >
              <LogoDesktop />
            </LogoLinkWrapper>
          </LogoContainer>

          {/* CTAs  */}
          <HeaderCTAs
            headerCTAsUID={props.headerCTAsUID || "default-ctas"}
            setHeaderCTAsData={setHeaderCTAsData}
          />

          {!noLinks && (
            <MobileSearchButton>
              <CustomButton
                kind="text"
                scale="small"
                cssClass="mobile-search-button"
                onClick={() => {
                  track("Open Header Search Box");
                  setIsSearchOpen(!isSearchOpen);
                }}
              >
                <CustomIcon
                  css={FA.MagnifyingGlassSolid}
                  isDarkMode={currentMode}
                  transitionPercent={currentTransitionPercent}
                />
              </CustomButton>
            </MobileSearchButton>
          )}

          <UserNav>
            {user.isReady && user.userData && user.isValid && !user.isExpired && (
              // signed in user
              <Dropdown alignment="right">
                <Dropdown.Trigger>
                  <AccountButton kind="text" scale="small">
                    {!isMobile && (
                      <CustomIcon
                        css={FA.UserCircle}
                        isDarkMode={currentMode}
                        transitionPercent={currentTransitionPercent}
                      />
                    )}
                    My Account
                    <CustomIcon
                      css={FA.ChevronDown}
                      isDarkMode={currentMode}
                      transitionPercent={currentTransitionPercent}
                    />
                  </AccountButton>
                </Dropdown.Trigger>
                <DropdownCustomContent>
                  <DropdownCustomOption
                    onClick={() => {
                      window.location.href = "/quoting/home";
                    }}
                  >
                    Dashboard
                  </DropdownCustomOption>
                  {user.userData.partnerId ? (
                    <DropdownCustomOption
                      onClick={() => {
                        window.location.href = `https://${workcenterBaseDomain}`;
                      }}
                    >
                      Workcenter
                    </DropdownCustomOption>
                  ) : (
                    <></>
                  )}
                  <DropdownCustomOption
                    onClick={() => {
                      window.location.href = "/quoting/history";
                    }}
                  >
                    Quotes
                  </DropdownCustomOption>
                  <DropdownCustomOption
                    onClick={() => {
                      window.location.href = "/orders";
                    }}
                  >
                    Orders
                  </DropdownCustomOption>
                  <DropdownCustomOption
                    onClick={() => {
                      window.location.href = "/quoting/parts";
                    }}
                  >
                    Parts Library
                  </DropdownCustomOption>
                  <DropdownCustomOption
                    onClick={() => {
                      window.location.href = "/user-settings";
                    }}
                  >
                    Account Settings
                  </DropdownCustomOption>
                  <DropdownCustomOption
                    onClick={() => {
                      window.location.href = "/logout";
                    }}
                  >
                    Log Out
                  </DropdownCustomOption>
                </DropdownCustomContent>
              </Dropdown>
            )}

            {/* Log in, if the user is not logged in or the token expired */}
            {user.isReady && (user.isExpired || !user.isValid) && (
              <LoginButton
                kind={"text"}
                onClick={() => {
                  window.location.href = "/login";
                }}
                scale="small"
                isDarkMode={currentMode}
                transitionPercent={currentTransitionPercent}
              >
                Log In / Register
              </LoginButton>
            )}
          </UserNav>

          {/* Mobile header toggle */}
          {!noLinks && (
            <MobileLinks tabIndex={0}>
              {isAccordionOpen ? (
                <CustomButton
                  kind={"text"}
                  scale="small"
                  onClick={() => setAccordionOpen(false)}
                  onKeyPress={() => setAccordionOpen(false)}
                >
                  <CustomIcon
                    css={FA.XMarkSolid}
                    isDarkMode={false}
                    transitionPercent={currentTransitionPercent}
                  />
                </CustomButton>
              ) : (
                <CustomButton
                  kind={"text"}
                  scale="small"
                  onClick={() => setAccordionOpen(true)}
                  onKeyPress={() => setAccordionOpen(true)}
                >
                  <CustomIcon
                    css={FA.BarsSolid}
                    isDarkMode={currentMode}
                    transitionPercent={currentTransitionPercent}
                  />
                </CustomButton>
              )}
            </MobileLinks>
          )}
        </HeaderUpperFlexbox>

        {!noLinks && (
          <HeaderLowerFlexbox>
            <HeaderLinksWrapper isOpen={isAccordionOpen}>
              <HeaderLinks
                headerLinksUID={props.headerLinksUID || "default-links"}
                setHeaderLinksData={setHeaderLinksData}
                isHeaderLinksDataSet={headerCTAsData ? true : false}
                isOpen={isAccordionOpen}
                showAnnouncementBanner={props.showAnnouncementBanner}
              />
              <SearchToggle
                onClick={() => {
                  track("Open Header Search Box");
                  setIsSearchOpen(!isSearchOpen);
                }}
              >
                <CustomIcon
                  css={FA.MagnifyingGlassSolid}
                  isDarkMode={currentMode}
                  transitionPercent={currentTransitionPercent}
                />
              </SearchToggle>
            </HeaderLinksWrapper>
          </HeaderLowerFlexbox>
        )}
      </HeaderBase>
      <Search
        isOpen={isSearchOpen && !props.disableSearch}
        showAnnouncementBanner={props.showAnnouncementBanner}
      >
        <SearchBarBackground
          isOpen={isSearchOpen && !props.disableSearch}
          showAnnouncementBanner={props.showAnnouncementBanner}
        >
          <SearchBarContainer>
            <InstantSearchBox fallback={<LoadingSearchSkeletonCard />} />
            <SearchCloseIcon
              css={FA.XMarkRegular}
              onClick={() => {
                track("Close Header Search Box");
                setIsSearchOpen(false);
              }}
            />
          </SearchBarContainer>
        </SearchBarBackground>
      </Search>
      <SearchOverlay
        isOpen={isSearchOpen && !props.disableSearch}
        showAnnouncementBanner={props.showAnnouncementBanner}
        isDarkMode={currentMode}
      />
    </>
  );
};

export default Header;
